import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { User, linkWithPopup, onIdTokenChanged } from 'firebase/auth';
import { auth, facebookProvider } from 'services/client/firebase';
import { syncLinkedProvider } from 'services/client/syncLinkedProvider';
import { getFirebaseErrorCode } from './useOAuthSignIn';

const FACEBOOK_PROVIDER_ID = 'facebook.com';

/** Whether a Firebase user already has Facebook attached (Firebase is the source of truth). */
const userHasFacebookLinked = (user: User | null): boolean =>
  user?.providerData.some(({ providerId }) => providerId === FACEBOOK_PROVIDER_ID) ?? false;

const getLinkErrorMessage = (code: string | undefined): string => {
  switch (code) {
    // The Facebook account is already attached to a *different* Bounce user — can't verify.
    case 'auth/credential-already-in-use':
    case 'auth/email-already-in-use':
      return 'That Facebook account is already linked to another Bounce account.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the popup. Please allow popups and try again.';
    // User dismissed the popup — nothing worth surfacing.
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return '';
    default:
      return "We couldn't verify with Facebook. Please try again.";
  }
};

interface Options {
  /** Called once Facebook is attached — whether freshly linked or already linked. */
  onLinked?: () => void;
}

/**
 * "Verify with Facebook" for the player-finder onboarding. The user is already signed in, so unlike
 * the sign-in linking flow (useOAuthAuthentication) this simply attaches the Facebook credential to
 * the current Firebase user via linkWithPopup, then reconciles the DB through the shared
 * link-provider endpoint. The resulting user_auth_identities row is what the "Verified with
 * Facebook" badge reads.
 */
export const useConnectFacebookIdentity = ({ onLinked }: Options = {}) => {
  const [isLinked, setIsLinked] = useState(() => userHasFacebookLinked(auth.currentUser));
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // auth.currentUser is null for a beat while Firebase restores the session, so the initial read
  // can miss an existing link. Subscribe so an already-linked account reflects as verified as soon
  // as the user resolves. (Display only — this does not re-sync the DB.)
  useEffect(
    () => onIdTokenChanged(auth, (user) => setIsLinked(userHasFacebookLinked(user))),
    [],
  );

  const markLinked = () => {
    setIsLinked(true);
    onLinked?.();
  };

  const connectFacebook = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError('Please sign in again to verify with Facebook.');
      return;
    }

    setIsLinking(true);
    setError(null);

    try {
      try {
        await linkWithPopup(currentUser, facebookProvider);
      } catch (linkError) {
        // Already attached (double-tap / return visit) is a success — re-throw anything else.
        if (getFirebaseErrorCode(linkError) !== 'auth/provider-already-linked') {
          throw linkError;
        }
      }

      // Record the link in the DB (user_auth_identities + latestAuthProvider, reconciled
      // server-side from Firebase). Best-effort: the link already succeeded, so a sync hiccup must
      // not surface to the user as a failure.
      await syncLinkedProvider('facebook').catch((syncError) =>
        Sentry.captureException(syncError, {
          extra: { context: 'useConnectFacebookIdentity:syncLinkedProvider' },
        }),
      );

      markLinked();
    } catch (linkError) {
      const message = getLinkErrorMessage(getFirebaseErrorCode(linkError));
      if (message) {
        setError(message);
        Sentry.captureException(linkError, {
          extra: { context: 'useConnectFacebookIdentity:linkWithPopup' },
        });
      }
    } finally {
      setIsLinking(false);
    }
  };

  return { connectFacebook, isLinking, isLinked, error };
};
